import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, User, ArrowRight } from 'lucide-react';
import { Header } from './Header';
import { SEO } from './SEO';

const BLOG_POSTS = [
  {
    id: '1',
    title: 'Voice AI Agents: Transforming Restaurant Phone Management',
    slug: 'voice-ai-agents-transforming-restaurant-phone-management',
    excerpt: 'Discover how Voice AI agents are revolutionizing restaurant phone management with advanced technology for reservations, orders, and customer service. Learn about seamless system integration and the ability to handle multiple calls simultaneously.',
    author: 'RestroCalls Team',
    published_at: '2025-03-27T00:00:00Z',
    tags: ['Voice AI', 'Restaurant Technology', 'Customer Service', 'Innovation']
  }
];

interface BlogProps {
  onContactClick: (e: React.MouseEvent) => void;
  onAuthClick: () => void;
  onDemoClick: () => void;
}

export function Blog({ onContactClick, onAuthClick, onDemoClick }: BlogProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-zinc-900">
      <SEO 
        title="Blog - RestroCalls.AI | Restaurant Technology Insights"
        description="Explore the latest insights on restaurant technology, AI phone systems, and customer service innovation. Learn how to transform your restaurant operations."
        path="/blog"
      />
      <Header 
        onContactClick={onContactClick}
        onAuthClick={onAuthClick}
        onDemoClick={onDemoClick}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <h1 className="text-4xl font-bold text-white mb-12">Latest Blog Posts</h1>
        <div className="grid gap-12">
          {BLOG_POSTS.map((post) => (
            <article 
              key={post.id}
              className="bg-zinc-900/50 rounded-2xl p-8 border border-zinc-800 hover:border-emerald-500/30 transition-all"
            >
              <h2 className="text-2xl font-bold text-white mb-4">
                <Link to={`/blog/${post.slug}`} className="hover:text-emerald-400 transition-colors">
                  {post.title}
                </Link>
              </h2>
              <div className="flex items-center gap-6 text-sm text-zinc-400 mb-6">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{new Date(post.published_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              </div>
              <p className="text-zinc-300 leading-relaxed mb-6">
                {post.excerpt}
              </p>
              <div className="flex items-center">
                <Link 
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  Read More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {post.tags.length > 0 && (
                <div className="flex items-center gap-2 mt-6">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm border border-emerald-500/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}